import { useEffect, useMemo, useState } from 'react';
import { formatDateISO, formatDateToLocal, formatToLocal } from '@safetyhub/shared';
import { Badge, Button, Card, TABLE_HEADER_CELL_CLASS } from '@safetyhub/ui';
import {
  getUserIdentifier,
  getInvitationKindLabel,
  getMemberKindBadgeTone,
  getInvitationStatusBadgeTone,
  isOwnerInvitation,
  memberRoleBadges,
  organizationsApi,
  InvitationStatus,
  MemberActivityStats,
  OrganizationInvitation,
  OrganizationMember,
  OrganizationMemberGroup,
} from '../../../lib/organizations';
import {
  ActivityIcon,
  BuildingIcon,
  CheckSquareIcon,
  SearchIcon,
  ShieldIcon,
  UsersIcon,
} from '../../../components/icons';
import { FilterPill } from '../../../components/FilterPill';
import { formatDayDistance } from '../../../utils/relativeDay';
import { useAuthStore } from '../../../stores/auth';
import { StatTile } from '../../../components/StatTile';
import { MembersLayoutProps, MembersHandlers } from './types';
import { PlusIcon, ClockIcon, XCircleIcon } from './components/Icons';
import { PersonAvatar } from '../../../components/PersonAvatar';
import { AllModals } from './components/Modals';
import { MemberDedupEntry } from './components/MemberDedupEntry';
import { memberDisplayName, memberSearchName } from '../../../lib/members';
import {
  ACTIVITY_FILTER_OPTIONS,
  ActivityFilter,
  buildMemberRows,
  countExpiringInvitations,
  countJoinedThisMonth,
  filterMemberRows,
  getAssignedSiteOptions,
  getInvitationsAccess,
  getInvitationsDisplayState,
  getMembersEmptyCopy,
  getMembersTabCounts,
  getPendingInvitationCount,
  getRoleDistribution,
  getRoleFilterOptions,
  INVITATIONS_FORBIDDEN_COPY,
  INVITATIONS_UNCOUNTABLE_VALUE,
  MEMBERS_TABS,
  MembersTab,
  shouldShowMemberGroups,
} from './membersView';

// ── 데스크톱 리스킨 공통 클래스 (목업 멤버관리 페이지 톤 — P3-1 문서관리 레퍼런스) ──
// 표 헤더 셀은 `@safetyhub/ui`의 TABLE_HEADER_CELL_CLASS로 승격했다.
// 아바타(목업 `.mem-av`)는 6색 팔레트를 쥔 `PersonAvatar`가 소유한다.
// 통계 카드(목업 `.stat`)는 결재함과 공유하는 `components/StatTile`이 소유한다.
// 필터 pill(목업 `.filter-dd`)은 문서관리와 공유하는 `components/FilterPill`이 소유한다.

// 역할 필터 옵션은 고정 목록이 아니라 **지금 목록에 등장하는 역할**에서 뽑는다
// (`getRoleFilterOptions` — 시드 12종 + 조직 생성 역할이라 손으로 열거할 수 없다, P7-b-2).

/** 할당 현장 칩을 몇 개까지 펼칠지 — 목업 `.sites-list`가 2개 + `+N`이다. */
const SITE_CHIP_VISIBLE = 2;

/**
 * 표의 최소 너비 — 표 카드 헤더와 `<table>`이 **같은 값**을 써야 가로 스크롤에서 둘이 함께
 * 움직인다. 그룹 칸(150px)이 서면 그만큼 넓어진다. Tailwind JIT가 스캔할 수 있도록 조립하지
 * 않고 완성된 클래스 문자열로 둔다.
 */
const TABLE_MIN_WIDTH_CLASS = {
  withGroups: 'min-w-[870px]',
  withoutGroups: 'min-w-[720px]',
} as const;

/**
 * 소속 그룹 칸 — 그룹이 둘 이상인 조직에서만 선다(`shouldShowMemberGroups`).
 *
 * 칩 색은 현장(slate)·역할(violet/blue)과 갈라 둔다. 한 행에 세 축의 분류가 나란히 서는데
 * 같은 색이면 "현장 하나 더"로 읽힌다. Owner는 GroupMember 행이 없어(묵시적 전 그룹) 비고,
 * 그룹 미지정 멤버도 같은 자리를 비운다 — 표의 다른 빈 칸과 같은 `-`로 침묵한다.
 */
function MemberGroupsCell({ groups }: { groups: OrganizationMemberGroup[] }) {
  if (groups.length === 0) {
    return <span className="text-sm text-ink-400">-</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {groups.map((group) => (
        <Badge
          key={group.id}
          tone="teal"
          shape="rounded"
          size="sm"
          className="max-w-[120px] truncate"
        >
          {group.name}
        </Badge>
      ))}
    </div>
  );
}

/**
 * 역할 분포 바·범례의 색 — 소유자 역할은 표의 배지와 같은 violet, 나머지는 파랑 계열을 돌려 쓴다(P7-b-2).
 *
 * 역할이 12종+라 종류 2색으로는 세그를 구분할 수 없어, 소유자만 배지 톤(`getMemberKindBadgeTone`: owner=violet)에
 * 고정하고 그 외는 등장 순서대로 팔레트를 순환한다. 시안 `.rb-bar .seg` hex(#a78bfa/#60a5fa)가 violet-400/blue-400이라
 * 첫 두 세그는 종전과 같은 색이다. Tailwind JIT가 스캔하도록 조립하지 않고 완성된 클래스 문자열로 둔다.
 */
const ROLE_SEGMENT_PALETTE = ['bg-blue-400', 'bg-emerald-400', 'bg-amber-400', 'bg-rose-400', 'bg-cyan-400', 'bg-indigo-400'];

function roleSegmentClass(roleKey: string, index: number): string {
  if (roleKey === 'owner') return 'bg-violet-400';
  return ROLE_SEGMENT_PALETTE[index % ROLE_SEGMENT_PALETTE.length];
}

/**
 * 초대 행 보조줄의 날짜 — `08.01` 꼴.
 *
 * 한 줄에 연락처와 두 개의 날짜(초대·만료)가 함께 서는 자리라 `formatDateToLocal`의
 * 긴 형(`2026년 8월 1일`)은 줄을 넘긴다. 그렇다고 `getMonth()`로 직접 자르면 실행 환경
 * 시간대를 타므로, 현지 변환은 `formatDateISO`(Asia/Seoul)에 맡기고 뒤 5글자만 취한다.
 */
function formatMonthDay(isoDate: string): string {
  return formatDateISO(new Date(isoDate)).slice(5).replace('-', '.');
}

/**
 * '마지막 활동' 셀 — 목업은 상대 표기("2일 전")를 굵게, 절대 시각을 아래 줄에 놓는다.
 * 값의 원천은 로그인 적재(User.lastLoginAt, 2026-08-02 시작)라 그 이전 가입자는 null —
 * 이력이 없는 것과 접속하지 않은 것을 구분할 수 없으므로 조용히 `-`로 둔다.
 */
function LastActivityCell({ lastLoginAt }: { lastLoginAt: string | null | undefined }) {
  if (!lastLoginAt) {
    return <span className="text-sm text-ink-400">-</span>;
  }
  const date = new Date(lastLoginAt);
  return (
    <div className="flex flex-col">
      <span className="text-sm font-bold text-ink-800">{formatDayDistance(date) ?? '-'}</span>
      <span className="mt-0.5 text-[12px] font-medium tabular-nums text-ink-400">
        {formatToLocal(date, {
          format: { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
        })}
      </span>
    </div>
  );
}

/** 초대 행의 이름 자리 — 아직 사람이 없으니 이름 대신 초대가 어디까지 갔는지를 놓는다. */
function getInvitationHeadline(status: InvitationStatus): string {
  if (status === 'REJECTED') return '초대 거절됨';
  if (status === 'EXPIRED') return '초대 만료됨';
  return '초대 수락 대기 중';
}

/** 상태 배지 라벨 — 종전 '초대 현황' 카드의 문구를 그대로 옮겼다. */
function getInvitationStatusLabel(status: InvitationStatus): string {
  if (status === 'REJECTED') return '거절됨';
  if (status === 'EXPIRED') return '만료됨';
  return '초대 대기';
}

/** 초대의 연락 수단 — 초대할 때 넣은 값이 곧 식별자다. */
function getInvitationIdentifier(invitation: OrganizationInvitation): string {
  return (
    invitation.email ||
    invitation.phone ||
    (invitation.username ? `@${invitation.username}` : '-')
  );
}

/** 가입 멤버 행 — 행 전체가 상세 모달로 가는 클릭 영역이다. */
function MemberTableRow({
  member,
  isCurrentUser,
  showGroups,
  onOpenDetail,
}: {
  member: OrganizationMember;
  isCurrentUser: boolean;
  showGroups: boolean;
  onOpenDetail: () => void;
}) {
  const withdrawnAt = member.user?.deletedAt;
  const assignedSites = member.siteAssignments ?? [];
  const siteCount = assignedSites.length;
  const visibleSites = assignedSites.slice(0, SITE_CHIP_VISIBLE);
  const hiddenSiteCount = siteCount - visibleSites.length;

  return (
    <tr onClick={onOpenDetail} className="cursor-pointer transition-colors hover:bg-surface-soft">
      <td className="px-4 py-3.5 whitespace-nowrap min-w-[280px]">
        <div className="flex items-center gap-3">
          <PersonAvatar name={memberSearchName(member) || undefined} seed={member.id} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[15px] font-bold tracking-tight text-ink-900">
                {memberDisplayName(member)}
              </span>
              {isCurrentUser && (
                <Badge tone="brand" shape="rounded" size="sm">
                  나
                </Badge>
              )}
            </div>
            <div className="mt-0.5 text-[12.5px] font-medium text-ink-500">
              {member.user ? getUserIdentifier(member.user.auths) : '-'}
            </div>
          </div>
        </div>
      </td>
      {/* 역할 배지 — 실제 역할 이름 전부(P7-b-2). 겸직이 정상이라 여러 개가 설 수 있어 줄바꿈을 허용한다 */}
      <td className="px-4 py-3.5">
        <div className="flex flex-wrap gap-1">
          {memberRoleBadges(member).map((badge) => (
            <Badge key={badge.key} tone={badge.tone} shape="rounded">
              {badge.label}
            </Badge>
          ))}
        </div>
      </td>
      {showGroups && (
        <td className="px-4 py-3.5 align-top min-w-[150px]">
          <MemberGroupsCell groups={member.groups ?? []} />
        </td>
      )}
      {/* 할당된 현장 — 목업 `.sites-cell`: 개수 + 현장명 칩(2개 + `+N`) */}
      <td className="px-4 py-3.5 align-top min-w-[230px]">
        {siteCount === 0 ? (
          <span className="text-sm font-semibold text-ink-400">현장 미할당</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-bold tabular-nums text-ink-800">{siteCount}개 현장</span>
            <div className="flex flex-wrap gap-1">
              {visibleSites.map((assignment) => (
                <Badge
                  key={assignment.id}
                  tone="slate"
                  shape="rounded"
                  size="sm"
                  className="max-w-[120px] truncate"
                >
                  {assignment.site?.name ?? '이름 없음'}
                </Badge>
              ))}
              {hiddenSiteCount > 0 && (
                // `+N`은 목업 `.site-chip.more`(brand) — 접힌 나머지가 있다는 신호라
                // 중립 칩과 색으로 갈라야 "현장 하나 더"로 오독되지 않는다.
                <Badge tone="brand" shape="rounded" size="sm">
                  +{hiddenSiteCount}
                </Badge>
              )}
            </div>
          </div>
        )}
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        <LastActivityCell lastLoginAt={member.user?.lastLoginAt} />
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        {withdrawnAt ? (
          // Badge는 title을 받지 않아(코어 prop 최소화) 툴팁은 래퍼가 소유.
          <span
            className="inline-flex cursor-help"
            title={`탈퇴일: ${formatDateToLocal(new Date(withdrawnAt))}`}
          >
            <Badge tone="rose" dot>
              탈퇴
            </Badge>
          </span>
        ) : member.user ? (
          <Badge tone="emerald" dot>
            활성
          </Badge>
        ) : (
          // 계정 미연동 멤버(서명 참가자 등 User 없는 Member) — 활성/탈퇴를 단정할
          // 근거(user.deletedAt)가 없으므로 배지 대신 무표시(종전 UI와 동일한 침묵).
          <span className="text-sm text-ink-400">-</span>
        )}
      </td>
    </tr>
  );
}

/**
 * 초대 행 — 같은 표에 서지만 **행 클릭이 없다**. 열어 볼 상세(멤버 프로필·현장 할당)가
 * 아직 없기 때문이고, 대신 상태 칸이 재발송·취소 버튼을 직접 쥔다(종전 '초대 현황'
 * 카드가 하던 일을 그대로 옮겨 왔다).
 */
function InvitationTableRow({
  invitation,
  isResending,
  showGroups,
  handlers,
}: {
  invitation: OrganizationInvitation;
  isResending: string | null;
  showGroups: boolean;
  handlers: MembersHandlers;
}) {
  const isPending = invitation.status === 'PENDING';
  const isRejected = invitation.status === 'REJECTED';

  return (
    <tr className={isPending ? undefined : 'bg-surface-soft'}>
      <td className="px-4 py-3.5 whitespace-nowrap min-w-[280px]">
        <div className="flex items-center gap-3">
          {/* 목업 `.mem-av.c-pending` — 미가입 자리를 점선 원으로 구분 */}
          <div
            className={`flex h-[42px] w-[42px] flex-none items-center justify-center rounded-full border-2 border-dashed ${
              isPending
                ? 'border-amber-200 bg-amber-50 text-amber-600'
                : isRejected
                  ? 'border-rose-200 bg-rose-50 text-rose-500'
                  : 'border-ink-300 bg-ink-100 text-ink-400'
            }`}
          >
            {isRejected ? <XCircleIcon className="h-5 w-5" /> : <ClockIcon className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            {/* 이름 자리의 이탤릭 — 사람 이름이 들어갈 칸에 상태 문구가 서 있다는 표시.
                같은 굵기·색으로 두면 '초대 수락 대기 중'이라는 이름의 멤버로 읽힌다. */}
            <div className="text-[15px] font-semibold italic tracking-tight text-ink-500">
              {getInvitationHeadline(invitation.status)}
            </div>
            <div className="mt-0.5 text-[12.5px] font-medium text-ink-500">
              {getInvitationIdentifier(invitation)}
              <span className="tabular-nums text-ink-400">
                {' · '}초대 {formatMonthDay(invitation.createdAt)}
                {isPending && ` · 만료 ${formatMonthDay(invitation.expiresAt)}`}
              </span>
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        <Badge tone={getMemberKindBadgeTone(isOwnerInvitation(invitation) ? 'owner' : 'member')} shape="rounded">
          {getInvitationKindLabel(invitation)}
        </Badge>
      </td>
      {/* 초대는 아직 그룹에 넣을 사람이 없다 — 합류 시점에 정해진다. */}
      {showGroups && (
        <td className="px-4 py-3.5 align-top min-w-[150px]">
          <span className="text-sm text-ink-400">-</span>
        </td>
      )}
      <td className="px-4 py-3.5 align-top min-w-[230px]">
        {invitation.site ? (
          <Badge tone="slate" shape="rounded" size="sm" className="max-w-[120px] truncate">
            {invitation.site.name}
          </Badge>
        ) : (
          <span className="text-sm font-semibold text-ink-400">현장 미할당</span>
        )}
      </td>
      {/* 초대는 아직 로그인할 수 없는 자리 — 목업도 이 칸을 `—`(미접속)로 비워 둔다. */}
      <td className="px-4 py-3.5 whitespace-nowrap">
        <span className="text-sm text-ink-400">-</span>
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <Badge tone={getInvitationStatusBadgeTone(invitation.status)} dot>
            {getInvitationStatusLabel(invitation.status)}
          </Badge>
          {isPending && (
            <>
              <Button
                size="sm"
                onClick={() => handlers.handleResendInvitation(invitation)}
                disabled={isResending === invitation.id}
              >
                {isResending === invitation.id ? '발송 중...' : '재발송'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => handlers.setCancelTarget(invitation)}
              >
                취소
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export function MembersDesktop({ data, modals, handlers }: MembersLayoutProps) {
  const { members, invitations, currentUserId } = data;
  const workspaceName = useAuthStore((state) => state.currentMembership?.company?.name);
  const companyId = useAuthStore((state) => state.currentMembership?.companyId);
  // 그룹 칸은 회사 그룹이 둘 이상일 때만 — 헤더 GroupSelector와 같은 원천(company.groups)을
  // 본다. 목록의 소속을 세면 아무도 없는 그룹이 빠져 두 UI의 판단이 갈린다.
  const companyGroupCount = useAuthStore(
    (state) => state.currentMembership?.company?.groups?.length ?? 0,
  );
  const showGroups = shouldShowMemberGroups(companyGroupCount);
  const tableMinWidthClass = showGroups
    ? TABLE_MIN_WIDTH_CLASS.withGroups
    : TABLE_MIN_WIDTH_CLASS.withoutGroups;
  const [tab, setTab] = useState<MembersTab>('all');
  const [query, setQuery] = useState('');
  // P7-b-2: 역할 key 필터(빈 문자열 = 전체). 옵션은 `getRoleFilterOptions`가 목록에서 뽑는다
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [siteFilter, setSiteFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('');

  // 평균 활동도 — 보조 정보라 실패해도 목록은 그대로 두고 카드 값만 비운다(결재함 loadStats와
  // 같은 태도). 멤버 목록과 달리 서버 집계(audit_logs)라 별도 왕복이 필요하다.
  const [activityStats, setActivityStats] = useState<MemberActivityStats | null>(null);
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    organizationsApi
      .getMemberActivityStats(companyId)
      .then((stats) => {
        if (!cancelled) setActivityStats(stats);
      })
      .catch((err) => {
        if (!cancelled) setActivityStats(null);
        console.error('Failed to load member activity stats:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const source = useMemo(() => ({ members, invitations }), [members, invitations]);

  /**
   * 초대 칸을 **셀 수 있나** — `member.invite`가 없으면 `invitations`는 빈 게 아니라 묻지 않은 것이다
   * (`membersView.getInvitationsAccess` 주석). 그래서 아래 네 자리(초대 대기 카드 값·만료 임박 pill·
   * '초대 대기' 탭 배지·빈 목록 문구와 표 줄 수)는 0을 「없음」으로 서술하지 않고 「볼 수 없음」으로
   * 가른다. 모바일도 **같은 함수**로 판정한다(각자 판정하면 같은 상태에 두 화면이 다르게 반응한다).
   */
  const invitationsAccess = getInvitationsAccess(data.canInviteMembers);

  // 탭 배지는 조직 전체 기준이라 검색·역할 필터를 켜도 줄지 않는다(그 수는 표 헤더가 보여준다).
  const tabCounts = useMemo(() => getMembersTabCounts(source), [source]);
  const pendingInvitationCount = useMemo(
    () => getPendingInvitationCount(invitations),
    [invitations],
  );
  const joinedThisMonth = useMemo(() => countJoinedThisMonth(members, new Date()), [members]);
  const expiringInvitations = useMemo(() => countExpiringInvitations(invitations), [invitations]);
  /**
   * 세 상태(`listed`·`empty`·`forbidden`)를 **자리마다** 만든다 — 같은 화면에서 '초대 대기' 카드는
   * PENDING만 세고 탭 배지는 초대 전량(최근 거절·만료 포함)을 세기 때문이다. 하나로 합치면 `empty`가
   * 어느 0인지 모르게 된다(`membersView.getInvitationsDisplayState` 주석).
   */
  const pendingCardState = getInvitationsDisplayState(invitationsAccess, pendingInvitationCount);
  const invitedTabState = getInvitationsDisplayState(invitationsAccess, tabCounts.invited);
  const roleDistribution = useMemo(() => getRoleDistribution(members), [members]);
  // 겸직이 있으면 세그 합 > total — 폭의 분모는 세그 합이다(위 바 주석)
  const roleSegmentTotal = useMemo(
    () => roleDistribution.items.reduce((sum, item) => sum + item.count, 0) || 1,
    [roleDistribution],
  );
  const roleFilterOptions = useMemo(() => getRoleFilterOptions(source), [source]);
  const siteOptions = useMemo(() => getAssignedSiteOptions(source), [source]);

  // 목록은 이미 전량 로드돼 있어 필터는 클라이언트에서 건다(서버 왕복 없음).
  const tabRows = useMemo(() => buildMemberRows(source, tab), [source, tab]);
  const visibleRows = useMemo(
    () => filterMemberRows(tabRows, { query, roleFilter, siteFilter, activityFilter }),
    [tabRows, query, roleFilter, siteFilter, activityFilter],
  );

  const isFiltered =
    query.trim().length > 0 || roleFilter !== '' || siteFilter !== '' || activityFilter !== '';
  // 지금 표의 줄 수를 셀 수 없나 — '초대 대기' 탭의 원천이 초대 목록이라 키가 없으면 0도 거짓이다.
  const rowCountUnknown = tab === 'invited' && invitedTabState === 'forbidden';

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">멤버 관리</h1>
          <p className="mt-1 text-[15px] font-medium text-ink-500">
            {/* 워크스페이스 이름은 "어느 조직의 멤버를 보고 있나"를 부제에서 답한다 —
                조직을 여러 개 가진 사용자가 전환 후에도 화면만 보고 확신할 수 있게. */}
            {workspaceName
              ? `조직 멤버를 초대하고 역할을 관리합니다 · ${workspaceName}`
              : '조직 멤버를 초대하고 역할을 관리합니다.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* 중복 인원 정리 (P5) — 클레임 조직 OWNER + 후보 존재 시에만 스스로 나타난다 */}
          {companyId && (
            <MemberDedupEntry companyId={companyId} onChanged={handlers.refreshMemberList} />
          )}
          {/* 서버 `POST :organizationId/members` = member.invite */}
          {data.canInviteMembers && (
            <Button variant="primary" onClick={() => handlers.setShowInviteModal(true)}>
              <PlusIcon className="h-5 w-5" />
              멤버 초대
            </Button>
          )}
        </div>
      </div>

      {/* 통계 카드 4장 — 앞 3장은 대응 탭 바로가기다. 카드 축과 탭 축이 정확히 같아,
          결재함처럼 부가 조건('내 결재만')을 함께 켜야 하는 카드가 없다.
          '평균 활동도'는 표시 전용(대응 탭 없음). 모바일은 2×2. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="전체 멤버"
          value={String(tabCounts.all)}
          unit="명"
          // 신규 합류가 0이면 붙일 말이 없다 — '이번 달 0명'은 자리만 차지한다.
          // pill과 foot이 같은 수(이번 달 신규)를 가리키는 건 시안 구조 그대로다(pill은
          // 눈길, foot은 설명).
          foot={joinedThisMonth > 0 ? `이번 달 ${joinedThisMonth}명 신규 합류` : undefined}
          pill={joinedThisMonth > 0 ? { kind: 'diff', diff: joinedThisMonth } : undefined}
          icon={UsersIcon}
          accentClass="text-brand-600"
          iconBgClass="bg-brand-50"
          selected={tab === 'all'}
          onSelect={() => setTab('all')}
        />
        <StatTile
          label="활성 멤버"
          value={String(tabCounts.active)}
          unit="명"
          icon={CheckSquareIcon}
          accentClass="text-emerald-600"
          iconBgClass="bg-emerald-50"
          selected={tab === 'active'}
          onSelect={() => setTab('active')}
        />
        <StatTile
          label="초대 대기"
          // 카드 값은 PENDING만이라 '초대 대기' **탭 배지**(최근 거절·만료 포함)와 갈릴 수
          // 있다. 카드는 지금 답을 기다리는 건(=조치할 대상)을 세고, 탭은 최근 초대 기록을
          // 훑는 자리라 범위가 다른 게 맞다.
          // `forbidden`이면 `0`이 아니라 `—`다 — 0을 쓰면 「보낸 초대가 없다」고 말하는 셈이다.
          // (`empty`는 실제로 0건이라 그대로 `0`을 쓴다 — 그게 두 상태를 가른 이유다.)
          value={
            pendingCardState === 'forbidden'
              ? INVITATIONS_UNCOUNTABLE_VALUE
              : String(pendingInvitationCount)
          }
          unit={pendingCardState === 'forbidden' ? undefined : '건'}
          // 값을 셀 수 없을 때는 그 자리에서 이유를 말한다(카드를 숨기면 기능의 존재조차 모른다).
          foot={pendingCardState === 'forbidden' ? INVITATIONS_FORBIDDEN_COPY : undefined}
          // 24시간 내 만료될 초대가 있으면 경고 — 재발송·재초대를 재촉하는 자리다.
          pill={
            pendingCardState !== 'forbidden' && expiringInvitations > 0
              ? { kind: 'alert', label: `만료 임박 ${expiringInvitations}` }
              : undefined
          }
          icon={ClockIcon}
          accentClass="text-amber-600"
          iconBgClass="bg-amber-50"
          selected={tab === 'invited'}
          onSelect={() => setTab('invited')}
        />
        <StatTile
          label="평균 활동도"
          // 로그인 적재(2026-08-02 시작) 기반이라 초기에는 값이 낮다 — 이력이 쌓이며 의미를
          // 갖는다. null(집계 대상 멤버 없음)과 로드 전·실패는 똑같이 '—'로 둔다.
          value={
            activityStats?.avgDailyLoginsPerMember != null
              ? String(activityStats.avgDailyLoginsPerMember)
              : '—'
          }
          unit={activityStats?.avgDailyLoginsPerMember != null ? '회/일' : undefined}
          foot={`최근 ${activityStats?.windowDays ?? 7}일 멤버당 로그인`}
          // 직전 창 대비 % — 서버가 직전 창 0(적재 초기 포함)이면 null을 줘 pill이 숨는다.
          pill={
            activityStats?.changePercent != null && activityStats.changePercent !== 0
              ? { kind: 'diff', diff: activityStats.changePercent, suffix: '%' }
              : undefined
          }
          icon={ActivityIcon}
          accentClass="text-ink-500"
          iconBgClass="bg-ink-100"
        />
      </div>

      {/* 역할 분포 — 시안 `.role-breakdown`. 라벨 + 누적바 + 범례 한 행. 축은 **실제 역할**이다(P7-b-2).
          겸직이 정상이라 세그 합이 멤버 수보다 클 수 있어, 폭은 total이 아니라 **세그 합**으로 나눈다
          (total로 나누면 바가 넘쳐 잘린다). 멤버 0명이면 바 자체가 거짓말(빈 회색 트랙)이라 행을 내보내지 않는다. */}
      {roleDistribution.total > 0 && (
        <Card
          radius="lg"
          padding="none"
          className="flex flex-wrap items-center gap-6 px-[22px] py-[18px]"
        >
          <span className="flex-none text-[13px] font-semibold text-ink-500">역할 분포</span>
          <div
            aria-hidden="true"
            className="flex h-[10px] min-w-[200px] max-w-[340px] flex-1 overflow-hidden rounded-full bg-line"
          >
            {roleDistribution.items.map(
              ({ role, count }, index) =>
                count > 0 && (
                  <div
                    key={role}
                    className={roleSegmentClass(role, index)}
                    style={{ width: `${(count / roleSegmentTotal) * 100}%` }}
                  />
                ),
            )}
          </div>
          <div className="flex flex-wrap items-center gap-[18px]">
            {roleDistribution.items.map(({ role, label, count }, index) => (
              <span
                key={role}
                className="inline-flex items-center gap-2 text-[13px] font-semibold text-ink-700"
              >
                <span
                  aria-hidden="true"
                  className={`h-[9px] w-[9px] flex-none rounded-[3px] ${roleSegmentClass(role, index)}`}
                />
                {label}
                <b className="font-extrabold tabular-nums text-ink-900">{count}</b>
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* 툴바 — 목업 `.toolbar`(검색 + 필터). 검색은 로드된 목록에 대한 클라이언트 필터다. */}
      <Card radius="lg" padding="none" className="flex flex-wrap items-center gap-2.5 px-4 py-3.5">
        <div className="flex h-[38px] min-w-[240px] flex-1 items-center gap-2 rounded-control border border-line bg-surface-soft px-3 transition-colors focus-within:border-brand-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-50">
          <SearchIcon className="h-4 w-4 flex-none text-ink-400" />
          <input
            type="text"
            aria-label="멤버 검색"
            placeholder="이름, 로그인 계정, 초대 연락처로 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[15px] text-ink-900 outline-none placeholder:text-ink-400"
          />
        </div>
        <FilterPill
          icon={ShieldIcon}
          label="역할"
          ariaLabel="역할 필터"
          value={roleFilter}
          options={roleFilterOptions}
          onChange={(value) => setRoleFilter(value)}
        />
        <FilterPill
          icon={BuildingIcon}
          label="담당 현장"
          ariaLabel="담당 현장 필터"
          value={siteFilter}
          options={siteOptions.map((site) => ({ value: site.id, label: site.name }))}
          onChange={setSiteFilter}
        />
        <FilterPill
          icon={ClockIcon}
          label="최근 활동"
          ariaLabel="최근 활동 필터"
          value={activityFilter}
          options={ACTIVITY_FILTER_OPTIONS.filter((option) => option.value !== '').map(
            (option) => ({ value: option.value, label: option.label }),
          )}
          onChange={(value) => setActivityFilter(value as ActivityFilter)}
        />
      </Card>

      {/* 탭 — 목록 범위의 정본. 배지는 검색·역할 필터와 무관한 전체 기준이다. */}
      <div
        className="flex items-end gap-1 border-b border-line"
        role="tablist"
        aria-label="멤버 목록 범위"
      >
        {MEMBERS_TABS.map(({ tab: tabKey, label }) => {
          const isActive = tabKey === tab;
          // '초대 대기' 배지의 원천은 초대 목록이라, 키가 없으면 셀 수 없다(0은 거짓이다).
          const badgeValue =
            tabKey === 'invited' && invitedTabState === 'forbidden'
              ? INVITATIONS_UNCOUNTABLE_VALUE
              : String(tabCounts[tabKey]);
          return (
            <button
              key={tabKey}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setTab(tabKey)}
              className={`-mb-px inline-flex items-center gap-[7px] border-b-2 px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                isActive
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-ink-500 hover:text-ink-800'
              }`}
            >
              {label}
              <Badge
                tone={isActive ? 'brand' : 'slate'}
                size="sm"
                className="justify-center min-w-[22px] tabular-nums"
              >
                {badgeValue}
              </Badge>
            </button>
          );
        })}
      </div>

      {/* 멤버 목록 — 선택된 탭의 범위. 초대도 같은 표의 행으로 선다(별도 '초대 현황' 카드
          없음): 초대는 "아직 자리에 앉지 않은 멤버"라, 목록을 갈라 두면 조직 인원을 두 곳에서
          세게 된다. */}
      <Card radius="lg" padding="none" className="overflow-x-auto">
        {/* 표 카드 헤더 — 목업 `.table-card-h`. 표와 같은 min-w를 둬 가로 스크롤 시 함께 움직인다 */}
        <div
          className={`${tableMinWidthClass} border-b border-line-soft bg-gradient-to-b from-white to-surface-soft px-4 py-3.5 text-sm font-semibold text-ink-500`}
        >
          총{' '}
          <span className="font-bold tabular-nums text-ink-800">
            {rowCountUnknown ? INVITATIONS_UNCOUNTABLE_VALUE : visibleRows.length}
          </span>
          명의 멤버
          {isFiltered && !rowCountUnknown && (
            <span className="ml-1 text-ink-400">(전체 {tabRows.length}명 중)</span>
          )}
        </div>
        <table className={`${tableMinWidthClass} w-full`}>
          <thead>
            <tr>
              <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[280px]`}>멤버</th>
              <th className={TABLE_HEADER_CELL_CLASS}>역할</th>
              {showGroups && (
                <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[150px]`}>그룹</th>
              )}
              <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[230px]`}>할당된 현장</th>
              <th className={TABLE_HEADER_CELL_CLASS}>마지막 활동</th>
              <th className={TABLE_HEADER_CELL_CLASS}>상태</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft bg-white">
            {visibleRows.length === 0 && (
              <tr>
                <td
                  colSpan={showGroups ? 6 : 5}
                  className="px-4 py-12 text-center text-sm font-medium text-ink-400"
                >
                  {getMembersEmptyCopy(tab, invitationsAccess)}
                </td>
              </tr>
            )}
            {visibleRows.map((row) =>
              row.kind === 'member' ? (
                <MemberTableRow
                  key={row.id}
                  member={row.member}
                  isCurrentUser={row.member.userId != null && row.member.userId === currentUserId}
                  showGroups={showGroups}
                  onOpenDetail={() => handlers.setDetailTarget(row.member)}
                />
              ) : (
                <InvitationTableRow
                  key={row.id}
                  invitation={row.invitation}
                  isResending={modals.isResending}
                  showGroups={showGroups}
                  handlers={handlers}
                />
              ),
            )}
          </tbody>
        </table>
      </Card>

      {/* 모달들 */}
      <AllModals modals={modals} handlers={handlers} data={data} />
    </div>
  );
}

export default MembersDesktop;
